import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, timeAgo } from "@/lib/api";
import { PageHead, StatusBadge } from "@/pages/AppLayout";
import { useAuth } from "@/context/AuthContext";
import { Truck, AlertTriangle, Clock, Plus, Wrench, ArrowUpRight, Activity } from "lucide-react";

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div className="card p-6 border-t-4" style={{ borderTopColor: accent }} data-testid={`kpi-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-[#94A3B8]">{label}</p>
          <p className="font-mono text-5xl mt-2" style={{ color: accent }}>{value}</p>
        </div>
        <Icon className="w-6 h-6 text-[#94A3B8]" />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, isManager } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard").then(({ data }) => setData(data)); }, []);

  if (!data) return <div className="p-6 md:p-10 font-head text-2xl text-[#FFB800] uppercase tracking-widest">Loading…</div>;

  const overdueWo = data.overdue_work_orders || 0;
  const overdueRem = data.overdue || 0;
  const showBanner = isManager && (overdueWo > 0 || overdueRem > 0);

  return (
    <div className="p-5 md:p-10 pb-24" data-testid="dashboard-page">
      <PageHead title={`G'day, ${user?.name?.split(" ")[0]}`} sub="Your fleet at a glance.">
        <Link to="/machines/new" className="btn-signal h-12 px-5 font-head flex items-center gap-2" data-testid="dashboard-add-machine"><Plus className="w-4 h-4" /> Add Machine</Link>
      </PageHead>

      {showBanner && (
        <div className="mb-4 border border-[#FF3B30]/50 bg-[#FF3B30]/10 p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="overdue-banner">
          <div className="flex items-center gap-3 flex-1">
            <AlertTriangle className="w-6 h-6 text-[#FF3B30] shrink-0" />
            <div>
              <p className="font-head font-800 uppercase tracking-tight text-lg text-[#FF3B30]">Needs Attention</p>
              <p className="font-body text-sm text-white/90">
                {overdueWo > 0 && <span data-testid="overdue-wo-count">{overdueWo} overdue work order{overdueWo !== 1 ? "s" : ""}</span>}
                {overdueWo > 0 && overdueRem > 0 && " · "}
                {overdueRem > 0 && <span data-testid="overdue-rem-count">{overdueRem} overdue reminder{overdueRem !== 1 ? "s" : ""}</span>}
              </p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            {overdueWo > 0 && <Link to="/work-orders" className="btn-signal h-10 px-4 font-head text-xs flex items-center" data-testid="banner-view-work-orders">View Work Orders</Link>}
            {overdueRem > 0 && <Link to="/reminders" className="btn-ghost h-10 px-4 font-head text-xs flex items-center" data-testid="banner-view-reminders">View Reminders</Link>}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <Kpi label="Total Machines" value={data.total_machines} icon={Truck} accent="#FFB800" />
        <Kpi label="Overdue" value={data.overdue} icon={AlertTriangle} accent="#FF3B30" />
        <Kpi label="Upcoming" value={data.upcoming} icon={Clock} accent="#00E676" />
      </div>

      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-4">
        {/* Fleet list */}
        <div className="card">
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <h2 className="font-head font-700 text-xl uppercase tracking-wide">Fleet Overview</h2>
            <Link to="/machines" className="font-body text-sm text-[#FFB800] flex items-center gap-1">View all <ArrowUpRight className="w-4 h-4" /></Link>
          </div>
          {data.machines.length === 0 ? (
            <div className="p-10 text-center">
              <Truck className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
              <p className="font-body text-[#94A3B8]">No machines yet. Add your first machine to get started.</p>
              <Link to="/machines/new" className="btn-signal inline-flex h-11 px-5 font-head text-sm items-center gap-2 mt-4"><Plus className="w-4 h-4" /> Add Machine</Link>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {data.machines.slice(0, 6).map((m) => (
                <Link to={`/machines/${m.id}`} key={m.id} className="flex items-center gap-4 px-6 py-4 hover:bg-[#23262F] transition-colors" data-testid={`dash-machine-${m.id}`}>
                  <div className="w-11 h-11 bg-[#0D0E11] border border-white/10 flex items-center justify-center shrink-0"><Wrench className="w-5 h-5 text-[#FFB800]" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="font-head font-700 text-lg truncate">{m.make} {m.model}</p>
                    <p className="font-mono text-xs text-[#94A3B8] uppercase">{m.type} · {m.running_hours || 0}h</p>
                  </div>
                  <StatusBadge status={m.health} />
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Activity feed */}
        <div className="card">
          <div className="flex items-center gap-2 px-6 py-4 border-b border-white/10">
            <Activity className="w-5 h-5 text-[#FFB800]" />
            <h2 className="font-head font-700 text-xl uppercase tracking-wide">Activity Feed</h2>
          </div>
          {data.activity.length === 0 ? (
            <p className="p-6 font-body text-[#94A3B8] text-sm">No activity yet. Logged services will show here.</p>
          ) : (
            <div className="divide-y divide-white/10 max-h-[520px] overflow-y-auto">
              {data.activity.map((a) => (
                <Link to={`/machines/${a.machine_id}`} key={a.id} className="block px-6 py-4 hover:bg-[#23262F] transition-colors" data-testid={`activity-${a.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-body text-sm"><span className="text-[#FFB800] font-700">{a.by}</span> logged <span className="font-700">{a.service_type}</span></p>
                    <span className="font-mono text-[10px] text-[#94A3B8] shrink-0">{timeAgo(a.when)}</span>
                  </div>
                  <p className="font-mono text-xs text-[#94A3B8] mt-1">{a.machine_name} · ${a.cost || 0}</p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
